import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {Product} from "../../../../models/product";
import {ShopService} from "../../service/shop.service";
import {CartService} from "../../../cart/service/cart.service";
import {AnalyticsService} from "../../../../services/util/analytics.service";

/**
 * "Често се купува заедно": this product plus what other buyers ordered with it, all ticked,
 * added to the cart with one button.
 */
@Component({
    selector: 'app-bought-together',
    standalone: true,
    template: `
        @if (others.length > 0) {
            <section class="mt-2 mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 text-left">
                <h2 class="font-semibold text-gray-900">Често се купува заедно</h2>
                <ul class="mt-3 space-y-2 list-none p-0">
                    @for (item of [product].concat(others); track item.id; let first = $first) {
                        <li class="flex items-center gap-3">
                            <input type="checkbox" class="h-5 w-5 shrink-0 accent-primaryDark"
                                   [id]="'together-' + item.id"
                                   [checked]="isSelected(item)"
                                   (change)="toggle(item)">
                            <img class="h-12 w-12 shrink-0 object-contain bg-white rounded" [src]="item.images[0]" [alt]="item.name"
                                 width="48" height="48" loading="lazy">
                            <label class="min-w-0 flex-1 text-sm text-gray-900" [for]="'together-' + item.id">
                                @if (first) {
                                    <span class="font-medium">Овој производ:</span>
                                }
                                <span class="line-clamp-2">{{ item.name }}</span>
                            </label>
                            <span class="shrink-0 text-sm font-semibold text-gray-900">{{ item.price }} ден.</span>
                        </li>
                    }
                </ul>
                <div class="mt-3 flex flex-wrap items-center justify-between gap-3">
                    <p class="text-sm text-gray-700">Вкупно: <strong class="text-base text-gray-900">{{ total }} ден.</strong></p>
                    <button type="button"
                            class="rounded bg-primaryDark px-4 py-2 text-sm font-medium text-white hover:bg-primaryDarker disabled:opacity-50"
                            [disabled]="selected.size === 0"
                            (click)="addSelected()">{{ buttonText }}</button>
                </div>
            </section>
        }
    `
})
export class BoughtTogetherComponent implements OnChanges {

    @Input({required: true})
    product!: Product;

    /** The products went into the cart - the page opens the cart overview. */
    @Output()
    added = new EventEmitter<void>();

    others: Product[] = [];

    selected = new Set<number>();

    constructor(private readonly shopService: ShopService,
                private readonly cartService: CartService,
                private readonly analytics: AnalyticsService) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['product'] || !this.product?.id) {
            return;
        }
        this.others = [];
        this.shopService.getBoughtTogether(this.product.id, 2).subscribe({
            next: products => {
                this.others = products.filter(product => product.id !== this.product.id && product.images?.length);
                this.selected = new Set([this.product, ...this.others].map(product => product.id!));
            },
            error: () => this.others = []
        });
    }

    isSelected(product: Product): boolean {
        return this.selected.has(product.id!);
    }

    toggle(product: Product): void {
        if (this.selected.has(product.id!)) {
            this.selected.delete(product.id!);
        } else {
            this.selected.add(product.id!);
        }
    }

    get total(): number {
        return [this.product, ...this.others]
            .filter(product => this.selected.has(product.id!))
            .reduce((sum, product) => sum + product.price, 0);
    }

    get buttonText(): string {
        return this.selected.size > 1 ? `Додади ги сите ${this.selected.size} во кошничка` : 'Додади во кошничка';
    }

    addSelected(): void {
        for (const product of [this.product, ...this.others].filter(item => this.selected.has(item.id!))) {
            this.cartService.add({product, quantity: 1});
            this.analytics.addToCart(product, 1);
        }
        this.added.emit();
    }
}
